import axiosInstance from "@/api/axios-instance";
import {
  OtpLogsResponse,
  OtpAnalytics,
  OtpAnalyticsOverview,
  OtpLogFilters,
  OtpLog,
} from "@/pages/otp-log/types/otp-log";
import {
  getMockOtpLogsResponse,
  getMockOtpLogById,
  getMockOtpLogsByUserId,
  getMockOtpAnalytics,
  getMockOtpAnalyticsOverview,
  getMockOtpStats,
  getMockOtpDeliveryRate,
  getMockOtpSuccessRate,
  getMockOtpChannelBreakdown,
} from "./mock-otp-data";

/**
 * Set to true to use mock data for demonstration purposes.
 * Set to false to use real API endpoints.
 * 
 * When switching to real API, ensure the backend endpoints are available:
 * - GET /v1/otp-log
 * - GET /v1/otp-log/:id
 * - GET /v1/otp-log/user/:userId
 * - GET /v1/otp-log/analytics
 * - GET /v1/otp-log/analytics/overview
 * - GET /v1/otp-log/analytics/delivery-rate
 * - GET /v1/otp-log/analytics/success-rate
 * - GET /v1/otp-log/analytics/channel-breakdown
 * - GET /v1/otp-log/stats
 */
const USE_MOCK_DATA = false;

export async function getOtpLogs(
  filters: OtpLogFilters
): Promise<OtpLogsResponse> {
  if (USE_MOCK_DATA) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));
    return getMockOtpLogsResponse(filters.page || 0, 20, {
      search: filters.search,
      event_type: filters.event_type,
      status: filters.status,
      channel: filters.channel,
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  }

  const params: Record<string, string> = {
    page: (filters.page || 0).toString(),
  };

  if (filters.search) params.search = filters.search;
  if (filters.event_type && filters.event_type !== "ALL")
    params.event_type = filters.event_type;
  if (filters.status && filters.status !== "ALL") params.status = filters.status;
  if (filters.channel && filters.channel !== "ALL")
    params.channel = filters.channel;
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.userId) params.userId = filters.userId;

  const response = await axiosInstance.get<OtpLogsResponse>("/otp-log", {
    params,
  });

  return response.data;
}

export async function getOtpLogById(id: string): Promise<OtpLog> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    const log = getMockOtpLogById(id);
    if (!log) {
      throw new Error("OTP log not found");
    }
    return log;
  }

  const response = await axiosInstance.get<OtpLog>(`/otp-log/${id}`);
  return response.data;
}

export async function getOtpLogsByUserId(userId: string): Promise<OtpLog[]> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return getMockOtpLogsByUserId(userId);
  }

  const response = await axiosInstance.get<OtpLog[]>(`/otp-log/user/${userId}`);
  return response.data;
}

export async function getOtpAnalytics(
  filters?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<OtpAnalytics> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 250));
    return getMockOtpAnalytics();
  }

  const params: Record<string, string> = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;

  const response = await axiosInstance.get<OtpAnalytics>("/otp-log/analytics", {
    params,
  });
  return response.data;
}

export async function getOtpAnalyticsOverview(
  filters?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<OtpAnalyticsOverview> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return getMockOtpAnalyticsOverview();
  }

  const params: Record<string, string> = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;

  const response = await axiosInstance.get<OtpAnalyticsOverview>(
    "/otp-log/analytics/overview",
    { params }
  );
  return response.data;
}

export async function getOtpDeliveryRate(
  filters?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<{ rate: number; delivered: number; sent: number }> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    return getMockOtpDeliveryRate();
  }

  const params: Record<string, string> = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;

  const response = await axiosInstance.get("/otp-log/analytics/delivery-rate", {
    params,
  });
  return response.data;
}

export async function getOtpSuccessRate(
  filters?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<{ rate: number; verified: number; delivered: number }> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    return getMockOtpSuccessRate();
  }

  const params: Record<string, string> = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;

  const response = await axiosInstance.get("/otp-log/analytics/success-rate", {
    params,
  });
  return response.data;
}

export async function getOtpChannelBreakdown(
  filters?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<{ SMS: number; EMAIL: number; WHATSAPP: number }> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    return getMockOtpChannelBreakdown();
  }

  const params: Record<string, string> = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;

  const response = await axiosInstance.get(
    "/otp-log/analytics/channel-breakdown",
    { params }
  );
  return response.data;
}

export async function getOtpStats(
  filters?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<OtpAnalyticsOverview> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return getMockOtpStats();
  }

  const params: Record<string, string> = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;

  const response = await axiosInstance.get("/otp-log/stats", { params });
  return response.data;
}

export async function resendOtp(logId: string): Promise<{ success: boolean; message: string }> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      success: true,
      message: "OTP resent successfully",
    };
  }

  const response = await axiosInstance.post(`/otp-log/${logId}/resend`);
  return response.data;
}

